import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Checkbox,
  alpha,
} from '@mui/material';
import {
  CalendarToday,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { AppLayout } from '@/components/layout';
import { useAggregatedTasks, type AggregatedTask } from '@/hooks';
import { todolistsService } from '@/services/collections/todolists';
import { brandColors } from '@/theme/colors';
import { PRIORITY_COLORS, formatDueDate } from '@/types/todolist';

export const TodayPage = () => {
  const navigate = useNavigate();
  const { todayGroups, loading, todayCount } = useAggregatedTasks('today');

  const handleToggleTask = async (task: AggregatedTask) => {
    await todolistsService.updateItem(task.listId, task.id, {
      completed: !task.completed,
    });
  };

  const handleTaskClick = (task: AggregatedTask) => {
    navigate(`/todo/${task.listId}`);
  };

  if (loading) {
    return (
      <AppLayout showBackToDashboard backToSection="todolists" title="Today">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  const { overdue = [], dueToday = [] } = todayGroups || {};
  const isEmpty = overdue.length === 0 && dueToday.length === 0;

  return (
    <AppLayout showBackToDashboard backToSection="todolists" title="Today">
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CalendarToday sx={{ fontSize: 32, color: brandColors.purple }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: brandColors.purple }}>
              Today
            </Typography>
            {todayCount > 0 && (
              <Chip
                label={todayCount}
                size="small"
                sx={{
                  bgcolor: brandColors.purple,
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          <Typography variant="body1" color="text.secondary">
            {new Date().toLocaleDateString('en-NZ', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Box>

        {isEmpty ? (
          <Paper
            sx={{
              textAlign: 'center',
              py: 8,
              bgcolor: alpha(brandColors.accent.green, 0.05),
              border: '1px dashed',
              borderColor: alpha(brandColors.accent.green, 0.3),
              borderRadius: 2,
            }}
          >
            <CheckCircle sx={{ fontSize: 48, color: brandColors.accent.green, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              All caught up!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You have no tasks due today. Enjoy your day!
            </Typography>
          </Paper>
        ) : (
          <>
            {/* Overdue Section */}
            {overdue.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Warning sx={{ fontSize: 20, color: brandColors.accent.red }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: brandColors.accent.red }}>
                    Overdue ({overdue.length})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {overdue.map((task) => (
                    <TaskRow
                      key={`${task.listId}-${task.id}`}
                      task={task}
                      onToggle={handleToggleTask}
                      onClick={handleTaskClick}
                      isOverdue
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Due Today Section */}
            {dueToday.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarToday sx={{ fontSize: 20, color: brandColors.purple }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: brandColors.purple }}>
                    Due Today ({dueToday.length})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {dueToday.map((task) => (
                    <TaskRow
                      key={`${task.listId}-${task.id}`}
                      task={task}
                      onToggle={handleToggleTask}
                      onClick={handleTaskClick}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Container>
    </AppLayout>
  );
};

// Task row component
const TaskRow = ({
  task,
  onToggle,
  onClick,
  isOverdue = false,
}: {
  task: AggregatedTask;
  onToggle: (task: AggregatedTask) => void;
  onClick: (task: AggregatedTask) => void;
  isOverdue?: boolean;
}) => {
  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(task);
  };

  return (
    <Paper
      onClick={() => onClick(task)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1.5,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: isOverdue ? alpha(brandColors.accent.red, 0.3) : 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: isOverdue ? brandColors.accent.red : brandColors.purple,
          boxShadow: 1,
        },
      }}
    >
      <Checkbox
        checked={task.completed}
        onClick={handleCheckboxClick}
        sx={{
          color: task.priority ? PRIORITY_COLORS[task.priority] : brandColors.purple,
          '&.Mui-checked': {
            color: brandColors.accent.green,
          },
        }}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0, mx: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            textDecoration: task.completed ? 'line-through' : 'none',
            color: task.completed ? 'text.disabled' : 'text.primary',
          }}
        >
          {task.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
          {task.dueDate && (
            <Chip
              size="small"
              label={formatDueDate(task.dueDate)}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: isOverdue
                  ? alpha(brandColors.accent.red, 0.1)
                  : alpha(brandColors.purple, 0.1),
                color: isOverdue ? brandColors.accent.red : brandColors.purple,
              }}
            />
          )}
          {task.priority && (
            <Chip
              size="small"
              label={task.priority}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: alpha(PRIORITY_COLORS[task.priority], 0.1),
                color: PRIORITY_COLORS[task.priority],
                textTransform: 'capitalize',
              }}
            />
          )}
        </Box>
      </Box>

      <Chip
        size="small"
        label={task.listName}
        variant="outlined"
        sx={{
          height: 24,
          fontSize: '0.7rem',
          borderColor: alpha(brandColors.purple, 0.3),
          color: 'text.secondary',
        }}
      />
    </Paper>
  );
};
